import React from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import useBusiness from '../hooks/useBusiness';
import { BusinessThemeProvider } from '../context/BusinessThemeContext';
import BusinessLogo from '../components/BusinessLogo';
import { BackSvg, ChatSvg } from '../components/BusinessIcons';
import { DISPLAY_FONT, FULL_SCREEN_SX, tok } from '../theme/businessTheme';
import { Business, formatAddress } from '../services/BusinessService';
import { BusinessNotFound } from './BusinessHomeScreen';

const cardSx = { background: tok('card'), border: `1px solid ${tok('cardBorder')}`, borderRadius: '20px', p: 2 };

const About: React.FC<{ business: Business }> = ({ business }) => {
  const navigate = useNavigate();
  const address = formatAddress(business.address);
  const chatAgent = business.agents[0];
  const tel = business.phone.replace(/[^\d+]/g, '');
  const site = business.website && /^https?:\/\//i.test(business.website) ? business.website : business.website ? `https://${business.website}` : '';

  return (
    <Box component="main" sx={{ ...FULL_SCREEN_SX, display: 'flex', flexDirection: 'column', background: tok('surface'), color: tok('onSurface') }}>
      <Box sx={{ position: 'relative', height: 132, flexShrink: 0, background: tok('background') }}>
        <ButtonBase
          onClick={() => navigate(`/b/${business.channelId}`)}
          aria-label="Back"
          sx={{ position: 'absolute', left: 20, top: 18, width: 44, height: 44, borderRadius: '22px', background: tok('secondaryAction'), color: tok('onSecondaryAction') }}
        >
          <BackSvg />
        </ButtonBase>
        <Box sx={{ position: 'absolute', left: 22, bottom: -36 }}>
          <BusinessLogo name={business.displayName} logoUrl={business.logoUrl} size={80} radius="24px" ring={tok('surface')} />
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2.75, pt: 6.5, pb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography component="h1" sx={{ fontFamily: DISPLAY_FONT, fontWeight: 800, fontSize: 34, lineHeight: 1, letterSpacing: '-0.03em', wordBreak: 'break-word' }}>
            {business.displayName}
          </Typography>
          {business.copy.eyebrow && (
            <Typography sx={{ fontSize: 14, color: tok('onSurfaceMuted'), mt: 0.75 }}>{business.category || business.copy.eyebrow}</Typography>
          )}
        </Box>
        {(business.about || business.summary) && (
          <Typography sx={{ fontSize: 15, lineHeight: 1.55, whiteSpace: 'pre-line' }}>{business.about || business.summary}</Typography>
        )}

        {business.hours && (
          <Box sx={cardSx}>
            <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Hours</Typography>
            <Typography sx={{ fontSize: 14, whiteSpace: 'pre-line', color: tok('onSurface') }}>{business.hours}</Typography>
          </Box>
        )}

        {(address || business.serviceArea) && (
          <Box sx={{ ...cardSx, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ flex: 1, fontSize: 14, minWidth: 0 }}>
              {address && <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{address}</Typography>}
              {business.serviceArea && <Typography sx={{ fontSize: 14, color: tok('onSurfaceMuted') }}>{business.serviceArea}</Typography>}
            </Box>
            {address && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: tok('accent'), fontWeight: 700, fontSize: 14, textDecoration: 'none', padding: '10px 4px' }}
              >
                Directions
              </a>
            )}
          </Box>
        )}

        {site && (
          <a href={site} target="_blank" rel="noopener noreferrer" style={{ color: tok('accent'), fontWeight: 600, fontSize: 15, wordBreak: 'break-all' }}>
            {business.website}
          </a>
        )}
      </Box>

      <Box sx={{ px: 2.25, pt: 1.75, pb: 'max(24px, env(safe-area-inset-bottom))', display: 'grid', gridTemplateColumns: tel ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 1.25, flexShrink: 0 }}>
        {chatAgent && (
          <ButtonBase
            component={RouterLink}
            to={`/form/${chatAgent.id}`}
            state={{ startMode: 'typed', fromBusiness: true }}
            sx={{ height: 54, borderRadius: '27px', background: tok('action'), color: tok('onAction'), fontSize: 16, fontWeight: 700, fontFamily: 'inherit', gap: 1 }}
          >
            <ChatSvg size={20} /> Message
          </ButtonBase>
        )}
        {tel && (
          <ButtonBase
            component="a"
            href={`tel:${tel}`}
            sx={{ height: 54, borderRadius: '27px', background: tok('card'), color: tok('onSurface'), border: `1px solid ${tok('cardBorder')}`, fontSize: 16, fontWeight: 700, fontFamily: 'inherit' }}
          >
            Call us
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
};

const BusinessAboutScreen: React.FC = () => {
  const { channelId } = useParams<{ channelId: string }>();
  const { business, loading } = useBusiness(channelId);
  return (
    <BusinessThemeProvider business={business}>
      {loading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: tok('background') }}>
          <CircularProgress aria-label="Loading" sx={{ color: tok('onBackground') }} />
        </Box>
      ) : !business.loaded ? (
        <BusinessNotFound />
      ) : (
        <About business={business} />
      )}
    </BusinessThemeProvider>
  );
};

export default BusinessAboutScreen;
